import SidesheetHeader from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-sidebar/components/sidesheet-header/SidesheetHeader";
import { useBlockedIntervals } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/BlockedIntervalsContext";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import deleteBlockedInterval from "services/scheduler/deleteBlockedInterval";
import queryBlockedIntervals from "services/scheduler/queryBlockedIntervals";
import {
  closeSidesheet,
  showEditBlockInterval,
} from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import BlockedIntervalTitleAndDates from "../../../blocked-interval-title-and-dates/BlockedIntervalTitleAndDates";
import SidesheetWarning from "../../../sidesheet-warning/SidesheetWarning";
import DeleteBlockedIntervalSeriesModal from "./delete-blocked-interval-series-modal/DeleteBlockedIntervalSeries";

import styles from "../../BlockedIntervalSidesheet.module.scss";

interface BlockedIntervalDetailsProps {
  blockedInterval: CalendarBlockedInterval;
}

const AMPLITUDE_COMPONENT_NAME = "blocked-interval-details";

const BlockedIntervalDetails: React.FC<BlockedIntervalDetailsProps> = ({
  blockedInterval,
}) => {
  const dispatch = useDispatch();
  const { timeRange } = useCalendarTime();
  const { forceReload } = useBlockedIntervals();

  const [error, setError] = useState<any>();
  const [
    showDeleteBlockedIntervalSeriesModal,
    setShowDeleteBlockedIntervalSeriesModal,
  ] = useState(false);

  const close = useCallback(() => {
    dispatch(closeSidesheet());
  }, [dispatch]);

  const handleEdit = useCallback(() => {
    track(
      PRO_CALENDAR_ANALYTICS_PAGE_NAME,
      [AMPLITUDE_COMPONENT_NAME, "edit-button"],
      "click",
    );
    dispatch(showEditBlockInterval(blockedInterval));
  }, [blockedInterval, dispatch]);

  const handleDelete = useCallback(async () => {
    if (blockedInterval?.repeat_rule) {
      setShowDeleteBlockedIntervalSeriesModal(true);
      return;
    } else {
      try {
        await deleteBlockedInterval(blockedInterval);
        await queryBlockedIntervals(timeRange);
        forceReload();
        close();
      } catch (error) {
        setError(error);
      }
    }
  }, [blockedInterval, timeRange, forceReload, close]);

  return (
    <>
      <SidesheetHeader
        title="Block"
        onClose={() => {
          track(
            PRO_CALENDAR_ANALYTICS_PAGE_NAME,
            [AMPLITUDE_COMPONENT_NAME, "close-button"],
            "click",
          );
          dispatch(closeSidesheet());
        }}
        onEdit={handleEdit}
        onDelete={handleDelete}
        className={getClasses(styles["title"])}
      />

      {error && <SidesheetWarning message={error.toString()} />}

      <div className={styles["body"]}>
        <BlockedIntervalTitleAndDates blockedInterval={blockedInterval} />
        {blockedInterval.admin_note && (
          <>
            <div className={styles["admin-note-title"]}>Admin Note</div>
            <div className={styles["admin-note"]}>
              {blockedInterval.admin_note}
            </div>
          </>
        )}
      </div>

      {showDeleteBlockedIntervalSeriesModal && (
        <DeleteBlockedIntervalSeriesModal
          show={showDeleteBlockedIntervalSeriesModal}
          onHide={() => setShowDeleteBlockedIntervalSeriesModal(false)}
          blockedInterval={blockedInterval}
          onConfirm={async () => {
            await queryBlockedIntervals(timeRange);
            forceReload();
            close();
          }}
        />
      )}
    </>
  );
};

export default BlockedIntervalDetails;
